import type { DateTime } from "luxon";
import type { BookableObjectPermissionWindowDto } from "../../../../features/Schedule/typings/resource-calendar.types";

type SelectedResource = {
  isPermitted?: boolean;
  permissionAssignees?: {
    windows?: BookableObjectPermissionWindowDto[];
  };
};

type CreateRange = {
  start: DateTime;
  end: DateTime;
};

/**
 * Ghost click/drag may start when some days are still bookable.
 * Vehicles without schedule windows stay fully blocked.
 */
export function canAttemptResourceCreate(
  selectedResource: SelectedResource | null | undefined
): boolean {
  if (selectedResource?.isPermitted !== false) {
    return true;
  }
  return (selectedResource.permissionAssignees?.windows?.length ?? 0) > 0;
}

function windowOverlapsDateRange(
  window: BookableObjectPermissionWindowDto,
  rangeStart: string,
  rangeEnd: string
): boolean {
  if (window.start > rangeEnd) {
    return false;
  }
  if (window.end && window.end < rangeStart) {
    return false;
  }
  return true;
}

function windowDeniesViewer(window: BookableObjectPermissionWindowDto): boolean {
  return window.isViewerPermitted !== true;
}

/**
 * Resource-view create is blocked when the selected interval overlaps a schedule
 * window the viewer cannot book. Days before that window stay bookable.
 */
export function isUnpermittedResourceCreate(
  selectedResource: SelectedResource | null | undefined,
  range?: CreateRange
): boolean {
  if (selectedResource?.isPermitted !== false) {
    return false;
  }

  const windows = selectedResource.permissionAssignees?.windows ?? [];
  if (windows.length === 0) {
    return true;
  }
  if (!range) {
    return true;
  }

  const startDate = range.start.toFormat("yyyy-MM-dd");
  const endDate = range.end.toFormat("yyyy-MM-dd");
  const rangeStart = startDate <= endDate ? startDate : endDate;
  const rangeEnd = startDate <= endDate ? endDate : startDate;

  return windows.some(
    window => windowOverlapsDateRange(window, rangeStart, rangeEnd) && windowDeniesViewer(window)
  );
}
